import React, { useState } from 'react';
import { useAuth, getAuthToken } from '../../auth/context/AuthContext';
import EmbedJobBoardCard from '../components/EmbedJobBoardCard';
import BrandColorCard from '../components/BrandColorCard';

const API = `${process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api'}/auth`;

export default function Settings() {
  const { user, setUser } = useAuth();
  const [editingCompany, setEditingCompany] = useState(false);
  const [companyForm, setCompanyForm] = useState({
    name: '', address_line1: '', address_line2: '', city: '', state: '', country: '', pincode: '', tax_id: '',
  });
  const [companyErr, setCompanyErr] = useState('');
  const [companyNote, setCompanyNote] = useState('');
  const [savingCompany, setSavingCompany] = useState(false);

  const authHeader = () => ({ Authorization: `Bearer ${getAuthToken()}` });

  const openCompanyEdit = () => {
    setCompanyErr('');
    setCompanyNote('');
    setCompanyForm({
      name: user?.company_name || '',
      address_line1: user?.address_line1 || '',
      address_line2: user?.address_line2 || '',
      city: user?.city || '',
      state: user?.state || '',
      country: user?.country || '',
      pincode: user?.pincode || '',
      tax_id: user?.tax_id || '',
    });
    setEditingCompany(true);
  };

  const saveCompany = async (e) => {
    e.preventDefault();
    setCompanyErr('');
    setCompanyNote('');
    setSavingCompany(true);
    try {
      const r = await fetch(`${API}/company-profile`, {
        method: 'PUT',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(companyForm),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Could not update the company profile');
      setUser(data);
      setEditingCompany(false);
      setCompanyNote('Company profile updated.');
    } catch (error) {
      setCompanyErr(error.message);
    }
    setSavingCompany(false);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Settings</div>
          <div className="page-subtitle">Manage your company profile and settings</div>
        </div>
      </div>

      <div style={{ maxWidth: user?.role === 'admin' ? 860 : 640, display: 'flex', flexDirection: 'column', gap: 20 }}>

        {user?.role === 'admin' && <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Company Profile</div>
            {user?.role === 'admin' && !editingCompany && (
              <button type="button" className="btn btn-sm" onClick={openCompanyEdit}>Update</button>
            )}
          </div>
          {companyErr && <div className="alert alert-error" style={{ marginBottom: 12 }}>{companyErr}</div>}
          {companyNote && !editingCompany && <div style={{ fontSize: 13, marginBottom: 12 }}>{companyNote}</div>}
          {editingCompany ? (
            <form onSubmit={saveCompany}>
              <label className="form-group">
                <div className="form-label">Company name</div>
                <input className="form-input" required value={companyForm.name} onChange={e => setCompanyForm({ ...companyForm, name: e.target.value })} style={{ width: '100%', boxSizing: 'border-box' }} />
              </label>
              <label className="form-group">
                <div className="form-label">Email</div>
                <input className="form-input" value={user?.email || ''} disabled readOnly style={{ width: '100%', boxSizing: 'border-box' }} />
              </label>
              <label className="form-group">
                <div className="form-label">Address line 1</div>
                <input className="form-input" required value={companyForm.address_line1} onChange={e => setCompanyForm({ ...companyForm, address_line1: e.target.value })} style={{ width: '100%', boxSizing: 'border-box' }} />
              </label>
              <label className="form-group">
                <div className="form-label">Address line 2</div>
                <input className="form-input" value={companyForm.address_line2} onChange={e => setCompanyForm({ ...companyForm, address_line2: e.target.value })} placeholder="Optional" style={{ width: '100%', boxSizing: 'border-box' }} />
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                <label className="form-group">
                  <div className="form-label">City</div>
                  <input className="form-input" required value={companyForm.city} onChange={e => setCompanyForm({ ...companyForm, city: e.target.value })} style={{ width: '100%', boxSizing: 'border-box' }} />
                </label>
                <label className="form-group">
                  <div className="form-label">State</div>
                  <input className="form-input" required value={companyForm.state} onChange={e => setCompanyForm({ ...companyForm, state: e.target.value })} style={{ width: '100%', boxSizing: 'border-box' }} />
                </label>
                <label className="form-group">
                  <div className="form-label">Country</div>
                  <input className="form-input" required value={companyForm.country} onChange={e => setCompanyForm({ ...companyForm, country: e.target.value })} style={{ width: '100%', boxSizing: 'border-box' }} />
                </label>
                <label className="form-group">
                  <div className="form-label">Pincode</div>
                  <input className="form-input" required value={companyForm.pincode} onChange={e => setCompanyForm({ ...companyForm, pincode: e.target.value })} style={{ width: '100%', boxSizing: 'border-box' }} />
                </label>
              </div>
              <label className="form-group">
                <div className="form-label">Tax ID</div>
                <input className="form-input" value={companyForm.tax_id} onChange={e => setCompanyForm({ ...companyForm, tax_id: e.target.value })} placeholder="GSTIN, EIN, or VAT" maxLength={40} style={{ width: '100%', boxSizing: 'border-box' }} />
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary btn-sm" disabled={savingCompany}>{savingCompany ? 'Saving…' : 'Save'}</button>
                <button type="button" className="btn btn-sm" onClick={() => { setEditingCompany(false); setCompanyErr(''); }}>Cancel</button>
              </div>
            </form>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[['Company Name', user?.company_name || user?.name], ['Company code', user?.company_code], ['Email', user?.email], ['Tax ID', user?.tax_id]].map(([label, val]) => (
                <div key={label} className="r-kv-row" style={{ display: 'flex', gap: 12 }}>
                  <div style={{ width: 130, fontSize: 13, color: 'var(--text-muted)', flexShrink: 0 }}>{label}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500 }}>{val || '—'}</div>
                </div>
              ))}
              <div className="r-kv-row" style={{ display: 'flex', gap: 12 }}>
                <div style={{ width: 130, fontSize: 13, color: 'var(--text-muted)', flexShrink: 0 }}>Address</div>
                <div style={{ fontSize: 13, color: 'var(--text-primary)', fontWeight: 500, whiteSpace: 'pre-line' }}>
                  {(user?.company_address || []).join('\n') || '—'}
                </div>
              </div>
            </div>
          )}
        </div>}

        <EmbedJobBoardCard />
        {user?.role === 'admin' && <BrandColorCard />}
      </div>
    </div>
  );
}
